import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Wrench, FileCheck, Eye, Check, X, Loader2, Mail, Phone, MapPin,
  Euro, ClipboardCheck, AlertTriangle, Building2,
} from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const STATUS_LABELS = {
  submitted: { label: "Zur Prüfung", color: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  approved:  { label: "Angenommen", color: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40" },
  rejected:  { label: "Abgelehnt",  color: "bg-red-500/15 text-red-300 border-red-500/40" },
  draft:     { label: "Entwurf",    color: "bg-zinc-700/30 text-zinc-300 border-zinc-600" },
};

export default function AdminInstallerApplications() {
  const [filter, setFilter] = useState("submitted");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null); // { user_id, name }
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/installer-applications?status=${filter}`);
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Bewerbungen nicht laden");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const approve = async (userId) => {
    if (!window.confirm("Bewerbung freigeben?\n\nDer Installateur wird aktiviert und kann sich einloggen.")) return;
    setActingId(userId);
    try {
      await api.post(`/admin/installer-applications/${userId}/approve`);
      toast.success("Bewerbung angenommen");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Freigabe fehlgeschlagen");
    } finally {
      setActingId(null);
    }
  };

  const doReject = async () => {
    if (!rejectFor || rejectReason.trim().length < 5) {
      toast.error("Bitte gib einen Grund mit mind. 5 Zeichen an.");
      return;
    }
    setActingId(rejectFor.user_id);
    try {
      await api.post(`/admin/installer-applications/${rejectFor.user_id}/reject`, { reason: rejectReason.trim() });
      toast.success("Bewerbung abgelehnt");
      setRejectFor(null);
      setRejectReason("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ablehnen fehlgeschlagen");
    } finally {
      setActingId(null);
    }
  };

  const openDoc = async (userId) => {
    // Use blob fetch so the API auth header is included (no token leak in URL)
    try {
      const resp = await api.get(`/admin/installer-applications/${userId}/insurance-doc`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Dokument nicht laden");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-installer-applications">
      <AppHeader title="Installateur-Bewerbungen" back="/dashboard" />
      <main className="max-w-5xl mx-auto px-3 sm:px-5 py-5 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Wrench className="text-[#00E575]" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">Installateur-Bewerbungen</h1>
              <p className="text-[12px] text-zinc-300 font-medium">Freigabe für neue Installateur-Accounts</p>
            </div>
          </div>
          <div className="flex rounded-xl bg-[#18181B] border border-zinc-800 overflow-hidden">
            {["submitted", "approved", "rejected", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`apps-filter-${s}`}
                className={`px-3 h-9 text-[12px] font-bold uppercase tracking-wider ${filter === s ? "bg-[#00E575] text-[#09090B]" : "text-zinc-400 hover:bg-zinc-800"}`}
              >
                {s === "submitted" ? "Offen" : s === "approved" ? "Genehmigt" : s === "rejected" ? "Abgelehnt" : "Alle"}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-400 inline-flex w-full justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> lädt…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">
            Keine Bewerbungen mit Status „{filter}".
          </div>
        )}

        {!loading && items.map((app) => {
          const u = app.user || {};
          const sa = app.service_area || {};
          const meta = STATUS_LABELS[app.status] || STATUS_LABELS.draft;
          return (
            <article key={app.id} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid={`app-card-${u.id}`}>
              <header className="flex flex-wrap items-start justify-between gap-3 mb-4 pb-3 border-b border-zinc-800">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-display text-lg font-bold">{u.first_name} {u.last_name}</h2>
                    <span className={`text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${meta.color}`}>{meta.label}</span>
                  </div>
                  {u.company_name && (
                    <div className="flex items-center gap-1.5 text-[12px] text-zinc-300 font-medium"><Building2 size={12} /> {u.company_name}</div>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[11.5px] text-zinc-400">
                    {u.email && <a href={`mailto:${u.email}`} className="inline-flex items-center gap-1 hover:text-zinc-200"><Mail size={11} /> {u.email}</a>}
                    {u.phone && <a href={`tel:${u.phone}`} className="inline-flex items-center gap-1 hover:text-zinc-200"><Phone size={11} /> {u.phone}</a>}
                  </div>
                </div>
                {app.status === "submitted" && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => approve(u.id)}
                      disabled={actingId === u.id}
                      data-testid={`app-approve-${u.id}`}
                      className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50"
                    >
                      {actingId === u.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                      Freigeben
                    </button>
                    <button
                      onClick={() => { setRejectFor({ user_id: u.id, name: `${u.first_name} ${u.last_name}` }); setRejectReason(""); }}
                      disabled={actingId === u.id}
                      data-testid={`app-reject-${u.id}`}
                      className="h-9 px-3 rounded-lg bg-red-500/15 text-red-300 border border-red-500/40 font-semibold text-[12px] inline-flex items-center gap-1.5 hover:bg-red-500/25 disabled:opacity-50"
                    >
                      <X size={13} /> Ablehnen
                    </button>
                  </div>
                )}
              </header>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[12.5px]">
                <Detail icon={ClipboardCheck} label="HWK-Nr." value={app.hwk_number} />
                <Detail icon={ClipboardCheck} label="HWK-Behörde" value={app.hwk_authority} />
                <Detail icon={MapPin} label="PLZ-Bereiche" value={(sa.plz_prefixes || []).join(", ") || "—"} />
                <Detail icon={MapPin} label="Anfahrt max." value={sa.radius_km ? `${sa.radius_km} km` : "—"} />
                <Detail icon={Euro} label="Stundensatz" value={app.hourly_rate ? `${app.hourly_rate} € brutto` : "—"} />
                <Detail icon={FileCheck} label="Versicherung gültig bis" value={app.insurance_expires_at || "—"} />
              </div>

              {app.references_text && (
                <div className="mt-3 bg-[#09090B] border border-zinc-800 rounded-lg p-3">
                  <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1">Referenzen / Qualifikationen</div>
                  <div className="text-[12.5px] text-zinc-200 whitespace-pre-line">{app.references_text}</div>
                </div>
              )}

              {app.insurance_doc_filename && (
                <button
                  onClick={() => openDoc(u.id)}
                  data-testid={`app-doc-${u.id}`}
                  className="mt-3 h-9 px-3 rounded-lg bg-[#09090B] border border-zinc-700 hover:border-[#00E575]/40 text-[12px] inline-flex items-center gap-1.5 font-medium"
                >
                  <Eye size={13} /> Versicherungsnachweis ansehen
                  <span className="text-zinc-300 text-[12px]">({app.insurance_doc_filename})</span>
                </button>
              )}

              {app.status === "rejected" && app.rejected_reason && (
                <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <div className="text-[12px] uppercase tracking-widest text-red-300 font-bold mb-1">Ablehnungsgrund</div>
                  <div className="text-[12.5px] text-zinc-100">{app.rejected_reason}</div>
                </div>
              )}

              {app.submitted_at && (
                <div className="mt-3 text-[10.5px] text-zinc-500">
                  Eingereicht: {new Date(app.submitted_at).toLocaleString("de-DE")}
                  {app.approved_at && <> · Genehmigt: {new Date(app.approved_at).toLocaleString("de-DE")}</>}
                  {app.rejected_at && <> · Abgelehnt: {new Date(app.rejected_at).toLocaleString("de-DE")}</>}
                </div>
              )}
            </article>
          );
        })}
      </main>

      {/* Reject modal */}
      {rejectFor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setRejectFor(null)} data-testid="app-reject-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-md w-full p-5 space-y-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-400" size={18} />
              <h2 className="font-display text-lg font-bold">Bewerbung ablehnen</h2>
            </div>
            <p className="text-[13px] text-zinc-300">
              <b>{rejectFor.name}</b> wird gesperrt und kann sich nicht mehr einloggen.
              Bitte gib einen Grund an (wird per E-Mail an den Bewerber gesendet).
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="app-reject-reason"
              rows={4}
              placeholder="z. B. HWK-Eintrag nicht verifizierbar, Versicherung abgelaufen, …"
              className="w-full bg-[#09090B] border border-zinc-700 rounded-lg p-3 text-zinc-50 focus:border-red-500/60 outline-none text-[13px]"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRejectFor(null)} className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-medium">Abbrechen</button>
              <button
                onClick={doReject}
                disabled={rejectReason.trim().length < 5 || actingId === rejectFor.user_id}
                data-testid="app-reject-confirm"
                className="h-9 px-4 rounded-lg bg-red-500/20 border border-red-500/50 text-red-200 font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-red-500/30 disabled:opacity-50"
              >
                {actingId === rejectFor.user_id ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                Ablehnen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="text-zinc-500 shrink-0 mt-0.5" size={13} />
      <div className="min-w-0">
        <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">{label}</div>
        <div className="text-zinc-100 truncate">{value || "—"}</div>
      </div>
    </div>
  );
}
